/**
 * Editable judgment badge component for review items
 * Phase 2 of Human-AI Collaborative Reviews implementation
 */

import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Loader2, Sparkles, Edit, User } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EditableJudgmentBadgeProps {
  value: string;
  reviewType: 'contract' | 'quantity' | 'calculation';
  claimId: string;
  itemId: string;
  isAiGenerated?: boolean;
  isModified?: boolean;
  lastModifiedBy?: string;
  isEditing?: boolean;
  disabled?: boolean;
  onEdit: (judgment: string) => void;
}

const JUDGMENT_OPTIONS = {
  contract: [
    { value: 'valid', label: 'Valid', color: 'bg-green-100 text-green-800 border-green-200' },
    { value: 'invalid', label: 'Invalid', color: 'bg-red-100 text-red-800 border-red-200' },
    { value: 'unclear', label: 'Unclear', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  ],
  quantity: [
    { value: 'valid', label: 'Valid', color: 'bg-green-100 text-green-800 border-green-200' },
    { value: 'excessive', label: 'Excessive', color: 'bg-red-100 text-red-800 border-red-200' },
    { value: 'unclear', label: 'Unclear', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  ],
  calculation: [
    { value: 'valid', label: 'Valid', color: 'bg-green-100 text-green-800 border-green-200' },
    { value: 'invalid', label: 'Invalid', color: 'bg-red-100 text-red-800 border-red-200' },
    { value: 'unclear', label: 'Unclear', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  ],
};

export function EditableJudgmentBadge({
  value,
  reviewType,
  claimId: _claimId,
  itemId: _itemId,
  isAiGenerated = true,
  isModified = false,
  lastModifiedBy,
  isEditing = false,
  disabled = false,
  onEdit,
}: EditableJudgmentBadgeProps) {
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  
  const options = JUDGMENT_OPTIONS[reviewType];
  const currentOption = options.find(opt => opt.value === value);
  
  const getStateIcon = () => {
    if (isEditing) {
      return <Loader2 className="h-3 w-3 animate-spin" />;
    }
    if (isModified) {
      return <Edit className="h-3 w-3" />;
    }
    if (isAiGenerated === false) {
      return <User className="h-3 w-3" />;
    }
    return <Sparkles className="h-3 w-3" />;
  };

  const getStateColor = () => {
    if (isModified) {
      return 'text-orange-600';
    }
    if (isAiGenerated === false) {
      return 'text-blue-600';
    }
    return 'text-blue-500';
  };

  const handleValueChange = (newValue: string) => {
    if (newValue !== value && !disabled && !isEditing) {
      onEdit(newValue);
    }
    setIsSelectOpen(false);
  };

  if (disabled || isSelectOpen) {
    return (
      <div className="flex items-center gap-2">
        <span className={cn("flex items-center gap-1 text-xs", getStateColor())}>
          {getStateIcon()}
        </span>
        
        <Select
          value={value}
          onValueChange={handleValueChange}
          open={isSelectOpen}
          onOpenChange={setIsSelectOpen}
          disabled={disabled || isEditing}
        >
          <SelectTrigger 
            className={cn(
              "h-7 w-fit px-2 text-xs font-medium border-0 focus:ring-1",
              currentOption?.color,
              isEditing && "opacity-50"
            )}
          >
            <SelectValue placeholder="Select judgment" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <div className="flex items-center gap-2">
                  <div className={cn("w-3 h-3 rounded-full", option.color)} />
                  <span>{option.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className={cn("flex items-center gap-1 text-xs", getStateColor())}>
        {getStateIcon()}
      </span>
      
      <Badge
        variant="outline"
        className={cn(
          "cursor-pointer transition-all duration-200 hover:scale-105",
          currentOption?.color,
          isEditing && "opacity-50 cursor-not-allowed",
          disabled && "cursor-not-allowed"
        )}
        onClick={() => {
          if (!disabled && !isEditing) {
            setIsSelectOpen(true);
          }
        }}
        title={
          isModified 
            ? `Modified by ${lastModifiedBy || 'user'}` 
            : isAiGenerated 
              ? 'AI generated - click to edit' 
              : 'Human created'
        }
      >
        {currentOption?.label || value}
      </Badge>
    </div>
  );
}